import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { CheckCircle, ArrowRight, Lock, ChevronLeft, ChevronRight, Calendar, Heart, Palette, X, GraduationCap, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { AVATARS, AvatarDisplay } from '@/lib/avatars';

const INTEREST_OPTIONS = [
  'Science', 'Math', 'History', 'Geography', 'Space', 'Animals',
  'Technology', 'Art', 'Music', 'Sports', 'Reading', 'Cooking',
  'Environment', 'Mythology', 'Coding', 'Puzzles'
];

const HOBBY_OPTIONS = [
  'Cricket', 'Drawing', 'Singing', 'Dancing', 'Swimming',
  'Cycling', 'Chess', 'Gardening', 'Photography', 'Writing',
  'Skating', 'Badminton', 'Painting', 'Crafts', 'Football', 'Yoga'
];

const BOARD_OPTIONS = [
  { id: 'CBSE', label: 'CBSE' },
  { id: 'ICSE', label: 'ICSE' },
  { id: 'State Board', label: 'State Board' },
  { id: 'International', label: 'International (IB/Cambridge)' },
  { id: 'Other', label: 'Other' },
];

const CLASS_OPTIONS = Array.from({ length: 12 }, (_, i) => ({
  id: `Class ${i + 1}`,
  label: `Class ${i + 1}`,
}));

function computeAgeBand(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) {
    age--;
  }
  if (age < 7) return '5-7';
  if (age < 9) return '7-9';
  if (age < 11) return '9-11';
  if (age < 13) return '11-13';
  return '13-15';
}

function computeAge(dobStr) {
  if (!dobStr) return null;
  const dob = new Date(dobStr);
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  if (today.getMonth() < dob.getMonth() || (today.getMonth() === dob.getMonth() && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
}

export default function ParentOnboardingCreateChild() {
  const navigate = useNavigate();
  const [step, setStep] = useState('child'); // 'child' | 'interests' | 'pin' | 'done'
  const [childData, setChildData] = useState({
    name: '', date_of_birth: '', avatar: '', gender: '', language_preference: 'auto',
    board: '', grade: '', interests: [], hobbies: []
  });
  const [pin, setPin] = useState(['', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '']);
  const [pinStep, setPinStep] = useState('set'); // 'set' | 'confirm'
  const [isCreating, setIsCreating] = useState(false);

  const toggleItem = (field, item) => {
    setChildData(prev => {
      const list = prev[field];
      return {
        ...prev,
        [field]: list.includes(item) ? list.filter(i => i !== item) : [...list, item]
      };
    });
  };

  const handleCreateChild = async () => {
    setIsCreating(true);
    try {
      await api.post('/children', {
        name: childData.name,
        date_of_birth: childData.date_of_birth,
        avatar: childData.avatar,
        gender: childData.gender || null,
        board: childData.board,
        grade: childData.grade,
        language_preference: childData.language_preference,
        interests: childData.interests,
        hobbies: childData.hobbies,
      });
      toast.success(`${childData.name}'s profile created!`);
      setStep('pin');
    } catch (error) {
      console.error('Failed to create child:', error);
      toast.error('Failed to create profile');
    } finally {
      setIsCreating(false);
    }
  };

  const handleBasicInfoNext = (e) => {
    e.preventDefault();
    if (!childData.name.trim()) {
      toast.error("Please enter your child's name");
      return;
    }
    if (!childData.date_of_birth) {
      toast.error('Please enter date of birth');
      return;
    }
    const age = computeAge(childData.date_of_birth);
    if (age < 3 || age > 18) {
      toast.error('Yukti is designed for children aged 5-15');
      return;
    }
    if (!childData.board) {
      toast.error('Please select your child\'s board');
      return;
    }
    if (!childData.grade) {
      toast.error('Please select your child\'s class');
      return;
    }
    if (!childData.avatar) {
      toast.error('Please pick an avatar for your child');
      return;
    }
    setStep('interests');
  };

  const handleInterestsNext = async () => {
    await handleCreateChild();
  };

  const handlePinInput = (index, value, isConfirm = false) => {
    if (value && !/^\d$/.test(value)) return;
    const arr = isConfirm ? [...confirmPin] : [...pin];
    arr[index] = value;
    isConfirm ? setConfirmPin(arr) : setPin(arr);
    if (value && index < 3) {
      const nextId = `${isConfirm ? 'confirm' : 'pin'}-${index + 1}`;
      document.getElementById(nextId)?.focus();
    }
  };

  const handlePinKeyDown = (index, e, isConfirm = false) => {
    if (e.key === 'Backspace') {
      const arr = isConfirm ? [...confirmPin] : [...pin];
      if (!arr[index] && index > 0) {
        const prevId = `${isConfirm ? 'confirm' : 'pin'}-${index - 1}`;
        document.getElementById(prevId)?.focus();
      }
    }
  };

  const handleSetPin = async () => {
    const pinStr = pin.join('');
    if (pinStr.length !== 4) {
      toast.error('Please enter all 4 digits');
      return;
    }
    if (pinStep === 'set') {
      setPinStep('confirm');
      setConfirmPin(['', '', '', '']);
      setTimeout(() => document.getElementById('confirm-0')?.focus(), 100);
      return;
    }
    const confirmStr = confirmPin.join('');
    if (pinStr !== confirmStr) {
      toast.error('PINs do not match. Try again.');
      setPinStep('set');
      setPin(['', '', '', '']);
      setConfirmPin(['', '', '', '']);
      setTimeout(() => document.getElementById('pin-0')?.focus(), 100);
      return;
    }
    try {
      await api.post('/auth/set-pin', { pin: pinStr });
      toast.success('PIN set!');
      setStep('done');
    } catch (error) {
      console.error('Failed to set PIN:', error);
      toast.error('Failed to set PIN');
    }
  };

  const handleSkipPin = () => setStep('done');

  const age = computeAge(childData.date_of_birth);
  const ageBand = computeAgeBand(childData.date_of_birth);

  // Max DOB: today, Min DOB: 18 years ago
  const today = new Date().toISOString().split('T')[0];
  const minDate = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0];

  if (step === 'done') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="max-w-md w-full">
          <div className="bg-white rounded-3xl p-10 text-center shadow-xl">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">All Set!</h1>
            <p className="text-gray-600 mb-8 text-lg">
              Tap your child's avatar from the home screen to start exploring.
            </p>
            <Button
              onClick={() => navigate('/family', { replace: true })}
              data-testid="go-to-kids-button"
              className="w-full bg-gradient-to-r from-amber-400 via-orange-400 to-pink-400 text-white h-14 rounded-xl text-lg font-bold shadow-lg"
            >
              Go to Kids <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (step === 'pin') {
    const currentPin = pinStep === 'set' ? pin : confirmPin;
    const isConfirm = pinStep === 'confirm';
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md w-full">
          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-400 to-indigo-500 rounded-full flex items-center justify-center shadow-md">
                <Lock className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {isConfirm ? 'Confirm Your PIN' : 'Set a Parent PIN'}
              </h1>
              <p className="text-gray-600 text-sm">
                {isConfirm ? 'Enter the same 4-digit PIN again' : 'This PIN keeps the parent dashboard kid-proof'}
              </p>
            </div>
            <div className="flex justify-center gap-4 mb-8">
              {[0, 1, 2, 3].map((i) => (
                <input
                  key={`${isConfirm ? 'confirm' : 'pin'}-${i}`}
                  id={`${isConfirm ? 'confirm' : 'pin'}-${i}`}
                  type="password" inputMode="numeric" maxLength={1}
                  value={currentPin[i]}
                  onChange={(e) => handlePinInput(i, e.target.value, isConfirm)}
                  onKeyDown={(e) => handlePinKeyDown(i, e, isConfirm)}
                  data-testid={`pin-input-${i}`}
                  className="w-14 h-16 text-center text-2xl font-bold border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:outline-none transition-colors"
                  autoFocus={i === 0}
                />
              ))}
            </div>
            <Button onClick={handleSetPin} data-testid="set-pin-btn" disabled={currentPin.some((d) => !d)}
              className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 text-white h-12 rounded-xl font-semibold shadow-md mb-3">
              {isConfirm ? 'Confirm PIN' : 'Next'}
            </Button>
            <Button variant="ghost" onClick={handleSkipPin} data-testid="skip-pin-btn" className="w-full text-gray-500 hover:text-gray-700">
              Skip for now
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (step === 'interests') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50 flex items-center justify-center px-4 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full">
          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <button onClick={() => setStep('child')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-to-basic">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <div className="text-center mb-6">
              <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-pink-400 to-rose-500 rounded-2xl flex items-center justify-center shadow-md">
                <Heart className="w-7 h-7 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-1">What does {childData.name} love?</h1>
              <p className="text-gray-500 text-sm">This helps Yukti personalize conversations (you can skip this)</p>
            </div>

            <div className="space-y-5">
              <div>
                <Label className="text-gray-700 font-semibold mb-3 block flex items-center gap-2">
                  <Heart className="w-4 h-4 text-rose-500" /> Interests
                </Label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_OPTIONS.map((item) => (
                    <button key={item} type="button" onClick={() => toggleItem('interests', item)} data-testid={`interest-${item.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        childData.interests.includes(item)
                          ? 'bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-md scale-105'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                      {item}
                    </button>
                  ))}
                </div>
                {childData.interests.length > 0 && (
                  <p className="text-xs text-gray-400 mt-2">{childData.interests.length} selected</p>
                )}
              </div>

              <div>
                <Label className="text-gray-700 font-semibold mb-3 block flex items-center gap-2">
                  <Palette className="w-4 h-4 text-indigo-500" /> Hobbies
                </Label>
                <div className="flex flex-wrap gap-2">
                  {HOBBY_OPTIONS.map((item) => (
                    <button key={item} type="button" onClick={() => toggleItem('hobbies', item)} data-testid={`hobby-${item.toLowerCase()}`}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        childData.hobbies.includes(item)
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-md scale-105'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}>
                      {item}
                    </button>
                  ))}
                </div>
                {childData.hobbies.length > 0 && (
                  <p className="text-xs text-gray-400 mt-2">{childData.hobbies.length} selected</p>
                )}
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <Button onClick={handleInterestsNext} data-testid="create-child-button" disabled={isCreating}
                className="flex-1 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white h-14 rounded-xl text-lg font-bold shadow-md">
                {isCreating ? 'Creating...' : 'Create Profile'}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // Step: child basic info
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-amber-50 flex items-center justify-center px-4 py-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg w-full">
        <div className="bg-white rounded-3xl p-8 shadow-xl">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Add Your Child</h1>
            <p className="text-gray-600 text-sm">Create a profile to start personalized learning</p>
          </div>

          <form onSubmit={handleBasicInfoNext} className="space-y-5">
            <div>
              <Label htmlFor="name" className="text-gray-700 font-semibold mb-2 block">Child's Name</Label>
              <Input id="name" data-testid="child-name-input" value={childData.name}
                onChange={(e) => setChildData({ ...childData, name: e.target.value })}
                placeholder="Enter name" className="border-2 border-amber-200 focus:border-orange-400 rounded-xl h-12" />
            </div>

            <div>
              <Label className="text-gray-700 font-semibold mb-2 block flex items-center gap-2">
                <Calendar className="w-4 h-4 text-orange-500" /> Date of Birth
              </Label>
              <Input type="date" data-testid="child-dob-input" value={childData.date_of_birth}
                onChange={(e) => setChildData({ ...childData, date_of_birth: e.target.value })}
                max={today} min={minDate}
                className="border-2 border-amber-200 focus:border-orange-400 rounded-xl h-12" />
              {age !== null && age < 3 && (
                <p className="text-xs text-red-500 mt-1.5">Child must be at least 3 years old</p>
              )}
            </div>

            <div>
              <Label className="text-gray-700 font-semibold mb-3 block">Gender</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  { id: 'girl', label: 'Girl' },
                  { id: 'boy', label: 'Boy' },
                  { id: '', label: 'Prefer not to say' },
                ].map((g) => (
                  <button key={g.id} type="button" onClick={() => setChildData({ ...childData, gender: g.id })}
                    data-testid={`gender-${g.id || 'skip'}`}
                    className={`py-2.5 rounded-xl font-semibold text-sm transition-all ${
                      childData.gender === g.id
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>
                    {g.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-blue-500" /> Board <span className="text-red-500">*</span>
                </Label>
                <Select value={childData.board} onValueChange={(v) => setChildData({ ...childData, board: v })}>
                  <SelectTrigger data-testid="board-select" className="border-2 border-amber-200 focus:border-orange-400 rounded-xl h-12">
                    <SelectValue placeholder="Select board" />
                  </SelectTrigger>
                  <SelectContent>
                    {BOARD_OPTIONS.map((b) => (
                      <SelectItem key={b.id} value={b.id} data-testid={`board-${b.id.toLowerCase().replace(/[\s/()]/g, '-')}`}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-green-500" /> Class <span className="text-red-500">*</span>
                </Label>
                <Select value={childData.grade} onValueChange={(v) => setChildData({ ...childData, grade: v })}>
                  <SelectTrigger data-testid="grade-select" className="border-2 border-amber-200 focus:border-orange-400 rounded-xl h-12">
                    <SelectValue placeholder="Select class" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASS_OPTIONS.map((c) => (
                      <SelectItem key={c.id} value={c.id} data-testid={`grade-${c.id.toLowerCase().replace(/\s/g, '-')}`}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-gray-700 font-semibold mb-3 block">Pick an Avatar</Label>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
                {AVATARS.map((av) => {
                  const Icon = av.icon;
                  const isSelected = childData.avatar === av.id;
                  return (
                    <motion.button key={av.id} type="button" whileTap={{ scale: 0.9 }}
                      onClick={() => setChildData({ ...childData, avatar: av.id })}
                      data-testid={`avatar-option-${av.id}`}
                      className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                        isSelected ? 'ring-3 ring-orange-400 bg-orange-50 scale-105' : 'hover:bg-gray-50'
                      }`}>
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${av.gradient} flex items-center justify-center shadow-md`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <span className="text-xs text-gray-600 font-medium">{av.label}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-gray-700 font-semibold mb-3 block">Preferred Language for Chat</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {[
                  { id: 'auto', label: 'Auto-detect' }, { id: 'english', label: 'English' },
                  { id: 'hindi', label: 'Hindi' }, { id: 'hinglish', label: 'Hinglish' },
                  { id: 'tamil', label: 'Tamil' }, { id: 'telugu', label: 'Telugu' },
                  { id: 'kannada', label: 'Kannada' }, { id: 'malayalam', label: 'Malayalam' },
                  { id: 'bengali', label: 'Bengali' }, { id: 'marathi', label: 'Marathi' },
                  { id: 'gujarati', label: 'Gujarati' },
                ].map((lang) => (
                  <button key={lang.id} type="button"
                    onClick={() => setChildData({ ...childData, language_preference: lang.id })}
                    data-testid={`lang-pref-${lang.id}`}
                    className={`py-2 px-1 rounded-lg font-medium text-xs transition-all ${
                      childData.language_preference === lang.id
                        ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}>
                    {lang.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Auto-detect responds in whatever language your child uses</p>
            </div>

            <Button type="submit" data-testid="next-to-interests-btn"
              className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white h-14 rounded-xl text-lg font-bold shadow-md">
              Next <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
